import { useEffect, useState, useMemo } from "react";
import "./UseCaseTemplate.scss";
import { UseCase, UseCaseTag, VidPartInput } from "../../../API";
import { Button, Select, Input } from "antd";
import { type SdkPlayerProps } from "../../../components/SdkPlayer";
import { getENV } from "../../../config";
import Stepes from "../../../components/Steps/Steps";
import Carousel, {
  type CarouselProps,
} from "../../../components/Carousel/Carousel";
import { IJSONObject } from "@blings/blings-player";
import { get } from "aws-amplify/api";
import { NEW_REST_API, REST_API_V2__GET_USE_CASES } from "../../../consts";
import { IAccountModel } from "../../../stores/accountStore";
import { useNavigate } from "react-router-dom";
import { PATHS, PROJECT_PATHS, toPath } from "../../../PATHS";

interface IProjectPublic {
  id: string | undefined;
  title: string;
  settings: string;
  videoParts: Array<VidPartInput>;
}
const steps = [
  {
    title: "Preview",
    content: "",
  },
  {
    title: "Generate",
    content: "",
  },
  {
    title: "Ready",
    content: "",
  },
];

// const MemoizedSdkPlayer = memo<SdkPlayerProps>(SdkPlayer);

export default function UseCaseTemplate({
  usecase,
  tags = [],
  accountId,
  accounts,
  instantiateUseCase,
  loadProjectInfoIntoProjectsArray,
}: {
  usecase: UseCase | null;
  tags: Array<UseCaseTag> | [];
  accountId: string;
  accounts: Array<IAccountModel> | [];
  instantiateUseCase: (
    accountId: string,
    useCaseId: string,
    title?: string
  ) => Promise<string | undefined>;
  loadProjectInfoIntoProjectsArray: (projectId: string) => void;
}) {
  const [current, setCurrent] = useState(0);
  const [isProjectNameValid, setIsProjectNameValid] = useState(true);
  const [renameProjectTitle, setRenameProjectTitle] = useState<string>("");
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isLoadingParts, setIsLoadingParts] = useState(true);
  const [newUseCaseProjectId, setNewUseCaseProjectId] = useState<string>("");
  const [liveControl, setLiveControl] = useState<IJSONObject>({});
  const [hasError, setHasError] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<
    string | undefined
  >(undefined);

  const multiAccounts = accounts.map((account) => ({
    label: account.name,
    value: account.id,
  }));

  const navigate = useNavigate();
  useEffect(() => {
    async function fetchProject() {
      if (!usecase) return;

      try {
        const response = await get({
          apiName: NEW_REST_API,
          path: `${REST_API_V2__GET_USE_CASES}/${usecase.id}/project`,
        }).response;

        const project =
          (await response.body.json()) as unknown as IProjectPublic;
        setLiveControl(JSON.parse(project.settings));
        setRenameProjectTitle(usecase.name);
        setIsLoadingParts(false);
      } catch (error) {
        console.error("Error fetching project or video parts:", error);
        setIsLoadingParts(false);
      }
    }
    fetchProject();
  }, [usecase]);

  const sdkParams = useMemo(() => {
    if (!usecase) return null;
    return {
      project: {
        id: usecase.projectId,
        env: getENV(),
        liveControlData: liveControl,
      },
      data: {},
      scenes: [{ lineup: usecase.lineupId || "" }],
      settings: {},
    } as SdkPlayerProps["sdkParams"];
  }, [usecase, liveControl]);

  const slides = useMemo(() => {
    return [
      ...(usecase?.images?.map((image) => ({
        type: "image",
        content: image,
      })) || []),
      {
        type: "player",
        content: sdkParams,
      },
    ] as CarouselProps["slides"];
  }, [usecase, sdkParams]);

  if (!usecase || !sdkParams) return null;

  const next = () => {
    setCurrent(current + 1);
  };

  const getTextBtn = () => {
    if (current === 0) {
      if (
        isSavingTemplate ||
        Object.keys(liveControl).length === 0 ||
        !isProjectNameValid
      )
        return "Loading...";
      return "Proceed with This Use Case";
    } else if (current === 1) return "Generate Project";
    else if (current === 2)
      if (isSavingTemplate) return "Generating Project...";
    return "Start Editing";
  };

  const items = steps.map((item) => ({
    key: item.title,
    label: item.title,
  }));

  const validateProjectName = (e: any) => {
    const { value } = e.target;
    setRenameProjectTitle(value);
    if (value.trim().length < 2) {
      setIsProjectNameValid(false);
    } else if (value.trim().length > 40) {
      setIsProjectNameValid(false);
    } else {
      setIsProjectNameValid(true);
    }
  };

  const handleInstantiateUseCase = async () => {
    try {
      setHasError(false);
      setIsSavingTemplate(true);

      const defaultAccountId = selectedAccountId || accountId;
      const newProjectId = await instantiateUseCase(
        defaultAccountId,
        usecase.id,
        renameProjectTitle
      );

      if (!newProjectId) {
        throw new Error("Project id is undefined");
      }
      await loadProjectInfoIntoProjectsArray(newProjectId);
      setNewUseCaseProjectId(newProjectId);
      next();
    } catch (e) {
      setHasError(true);
    }
    setIsSavingTemplate(false);
  };

  const handleNextBtn = async () => {
    if (current === 0) next();
    else if (current === 1) {
      handleInstantiateUseCase();
    } else if (current === 2) {
      navigate(
        toPath(PATHS.project, newUseCaseProjectId, PROJECT_PATHS.editContent)
      );
    }
  };

  const getStepContentRight = () => {
    switch (current) {
      case 0:
        return (
          <div className="use-case-title">
            <div className="use-case-description">
              <h1>{usecase.name}</h1>
              {tags.length > 0 && (
                <div className="tag-list">
                  {tags.map((tag) => (
                    <div key={tag.tag} className="usecase-tag">
                      {tag.tag}
                    </div>
                  ))}
                </div>
              )}
              <p>{usecase.shortDescription} </p>
            </div>
            <div className="use-case-instruction-next">
              <p>
                Proceed with this template to customize its visual and textual
                content, fonts, colors, branding, and more.
              </p>
            </div>
          </div>
        );
      case 1:
        return hasError || isSavingTemplate ? (
          <div className="step-done">
            <div className="preparing">
              <div className="title">
                {hasError ? "Generation failed" : "Generating..."}
              </div>
              <p className="message">
                {hasError
                  ? "Your project failed to generate. Please try again. If the issue persists, contact us for support."
                  : "Your Project is being prepared and will be ready for editing shortly."}
              </p>
            </div>
          </div>
        ) : (
          <div className="rename-wrapper">
            <div className="title">Name This Project</div>
            <div className="rename-input-wrapper">
              <div className="subtitle">
                Pick a name that helps you identify this use case later
              </div>
              <Input
                className="rename-input"
                placeholder="Project Name"
                value={renameProjectTitle}
                onChange={validateProjectName}
              />
            </div>
            {multiAccounts?.length > 1 && (
              <div className="select-account-wrapper">
                <div className="subtitle">
                  Select an account to use this template
                </div>
                <Select
                  placeholder="Select account"
                  className="select-account"
                  value={selectedAccountId}
                  options={multiAccounts}
                  onChange={(value) => {
                    setSelectedAccountId(value);
                  }}
                />
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="step-done">
            <div className="preparing">
              <div className="title">Congratulations!</div>
              <p className="message">Your project is ready for editing.</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const isNextBtnDisabled = () => {
    if (
      isSavingTemplate ||
      Object.keys(liveControl).length === 0 ||
      !isProjectNameValid
    )
      return true;
    if (current === 0) return false;
    else if (current === 1 && multiAccounts.length > 1 && !selectedAccountId)
      return true;
    else if (current === 2) return isSavingTemplate || hasError;
    return false;
  };

  return (
    <div className="use-case-main-wrapper">
      <div className="use-case-left-side">
        <Carousel
          slides={slides}
          showPagination={current === 0}
          isLoading={isLoadingParts}
        />
      </div>
      <div className="use-case-right-side">
        <div className="steps-wrapper">
          <div className="use-case-content">
            <Stepes steps={items} currentStep={current} />
            {getStepContentRight()}
          </div>
        </div>

        {current < steps.length && !hasError && (
          <div
            className="next-btn-wrapper"
            style={{ marginBottom: current === 0 ? "35px" : 0 }}
          >
            <Button
              className="next-step-btn"
              onClick={() => handleNextBtn()}
              disabled={isNextBtnDisabled()}
            >
              {getTextBtn()}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
